import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { DateTime, Settings } from 'luxon';
import {
  createCalendarCarousel,
  maxMenuSelectionDaysAhead,
} from '../helpers/carouselCalendarHelpers';
import { useDispatch } from 'react-redux';
import {
  getMealExchangeActiveDay,
  getSelectedDays,
} from '../../../../redux/selectors/orderFormSelector';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { mealExchangeChangeActiveDay } from '../../../../redux/actions/orderFormActions';
import {
  handleClickNextBackButton,
  isNextBackButtonDisabled,
} from '../helpers/mealExchangeHelpers';
import Button from '../../../common/Button';
import { useAppSelector } from '../../../../config/hooks';
import { useGetApiFormSettingsQuery } from '../../../../services/api/form/open/open.api';
import { classNamesUtil } from '../../../../utils/classNamesUtil.util';

const CarouselCalendar = ({ companyId }) => {
  const dispatch = useDispatch();

  const selectedDays = useAppSelector(getSelectedDays);
  const activeDay = useAppSelector(getMealExchangeActiveDay);

  const { data: formSettings } = useGetApiFormSettingsQuery(companyId, {
    skip: !companyId,
  });

  Settings.defaultLocale = 'pl';
  const local = DateTime.local;

  const handleChangeDay = (date, millis) => {
    if (
      !maxMenuSelectionDaysAhead(formSettings, date) &&
      selectedDays?.includes(millis)
    ) {
      dispatch(mealExchangeChangeActiveDay(date));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const [swiper, setSwiper] = useState(null);

  const slidePerView = {
    0: {
      slidesPerView: 3.5,
      spaceBetween: 8,
    },
    350: {
      slidesPerView: 5,
      spaceBetween: 8,
    },
    400: {
      slidesPerView: 5.5,
      spaceBetween: 8,
    },
    450: {
      slidesPerView: 6,
      spaceBetween: 8,
    },
    500: {
      slidesPerView: 6.5,
      spaceBetween: 8,
    },
    550: {
      slidesPerView: 7,
      spaceBetween: 8,
    },
    600: {
      slidesPerView: 7.5,
      spaceBetween: 8,
    },
    650: {
      slidesPerView: 8,
      spaceBetween: 8,
    },
    700: {
      slidesPerView: 8.5,
      spaceBetween: 8,
    },
    750: {
      slidesPerView: 9,
      spaceBetween: 8,
    },
    800: {
      slidesPerView: 9.5,
      spaceBetween: 8,
    },
    850: {
      slidesPerView: 10,
      spaceBetween: 8,
    },
    900: {
      slidesPerView: 10.5,
      spaceBetween: 8,
    },
    950: {
      slidesPerView: 11,
      spaceBetween: 8,
    },
    1000: {
      slidesPerView: 11.5,
      spaceBetween: 8,
    },
    1050: {
      slidesPerView: 12,
      spaceBetween: 8,
    },
    1100: {
      slidesPerView: 13,
      spaceBetween: 8,
    },
    1150: {
      slidesPerView: 14,
      spaceBetween: 8,
    },
    1200: {
      slidesPerView: 15,
      spaceBetween: 8,
    },
    1250: {
      slidesPerView: 17,
      spaceBetween: 8,
    },
  };

  useEffect(() => {
    const indexOfActiveDay = createCalendarCarousel(selectedDays)
      .map(item => item.toMillis())
      .indexOf(DateTime.fromISO(activeDay).toMillis());

    swiper?.slideTo(indexOfActiveDay);
  }, [activeDay, selectedDays, swiper]);

  return (
    <>
      <div className="carousel-calendar">
        <div className="container carousel-calendar__container">
          <div className="carousel-calendar__wrapper">
            <div className="carousel-calendar__overlay carousel-calendar__overlay--left" />
            <Button
              colorBlank
              containerClass="carousel-calendar__arrow carousel-calendar__arrow--left"
              disabled={isNextBackButtonDisabled(0, selectedDays, activeDay)}
              icon={<FontAwesomeIcon icon={faChevronLeft} />}
              onClick={() => {
                dispatch(
                  handleClickNextBackButton('back', activeDay, selectedDays)
                );
              }}
            />
            <Swiper
              breakpoints={slidePerView}
              centeredSlides
              centeredSlidesBounds
              longSwipesRatio={0.2}
              onSwiper={setSwiper}
              style={{
                paddingTop: 5,
              }}
              threshold={20}
            >
              {createCalendarCarousel(selectedDays)?.map(item => {
                const millis = item.toMillis();
                const day = item.day;
                const month = item.month;
                const year = item.year;
                const dayIso = item.toISODate();

                const available = selectedDays.includes(millis);

                return (
                  <SwiperSlide key={millis}>
                    <div
                      className={classNamesUtil('carousel-calendar__date', {
                        'carousel-calendar__date--active': activeDay === dayIso,
                        'carousel-calendar__date--available': available,
                        'carousel-calendar__date--outside-area body-m':
                          available &&
                          maxMenuSelectionDaysAhead(formSettings, dayIso),
                      })}
                      onClick={() => handleChangeDay(dayIso, millis)}
                      onKeyDown={() => handleChangeDay(dayIso, millis)}
                      role="button"
                      tabIndex="0"
                    >
                      <span>{local(year, month, day).weekdayShort}</span>
                      <div>{day}</div>
                      <span>{local(year, month, day).monthShort}</span>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <Button
              colorBlank
              containerClass="carousel-calendar__arrow carousel-calendar__arrow--right"
              disabled={isNextBackButtonDisabled(
                selectedDays.length - 1,
                selectedDays,
                activeDay,
                formSettings
              )}
              icon={<FontAwesomeIcon icon={faChevronRight} />}
              onClick={() => {
                dispatch(
                  handleClickNextBackButton('next', activeDay, selectedDays)
                );
              }}
            />
            <div className="carousel-calendar__overlay carousel-calendar__overlay--right" />
          </div>
        </div>
      </div>
    </>
  );
};

export default CarouselCalendar;
